import React, { useState, useEffect } from 'react';
import { MdArrowBack, MdLockOutline, MdEmail, MdTranslate, MdChevronRight } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { ref, get, update } from "firebase/database";
import { db } from "../firebase";
import { useTranslation } from 'react-i18next';
import i18n from '../i18n'; // Direct i18n instance config file se import kiya to avoid function error
import Swal from 'sweetalert2';
import './Settings.css';

const Settings = () => {
    const navigate = useNavigate();
    const { t } = useTranslation(); // Ab hum t() custom local hook se use karenge
    const [currentUser, setCurrentUser] = useState(null);
    const [currentLang, setCurrentLang] = useState({ code: 'en', name: 'English', flag: '🇺🇸' });

    const languages = [
        { code: 'en', name: 'English', flag: '🇺🇸' },
        { code: 'es', name: 'Spanish', flag: '🇪🇸' },
        { code: 'fa', name: 'Persian', flag: '🇮🇷' },
        { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
        { code: 'ar', name: 'Arabic', flag: '🇸🇦' }
    ];

    useEffect(() => {
        const savedUser = JSON.parse(localStorage.getItem('user'));
        if (savedUser) setCurrentUser(savedUser);

        // Saved global settings sync check
        const currentLangCode = i18n.language || 'en';
        const foundLang = languages.find(l => currentLangCode.startsWith(l.code));
        if (foundLang) setCurrentLang(foundLang);
    }, []);

    // 1. Password Change Logic
    const handleChangePassword = async () => {
        const { value: formValues } = await Swal.fire({
            title: t('change_password'),
            html:
                '<input id="oldPass" class="swal2-input" placeholder="Old Password" type="password">' +
                '<input id="newPass" class="swal2-input" placeholder="New Password" type="password">',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonColor: '#f3ba2f',
            preConfirm: () => {
                return [
                    document.getElementById('oldPass').value,
                    document.getElementById('newPass').value
                ]
            }
        });

        if (formValues) {
            const [oldPass, newPass] = formValues;
            if (!oldPass || !newPass) return Swal.fire('Error', 'Fields cannot be empty', 'error');

            const userRef = ref(db, `users/${currentUser.username}`);
            const snapshot = await get(userRef);
            
            if (snapshot.exists() && snapshot.val().password === oldPass) {
                await update(userRef, { password: newPass });
                Swal.fire('Success', 'Password updated successfully!', 'success');
            } else {
                await Swal.fire('Error', 'Old password is incorrect', 'error');
            }
        }
    };

    // 2. Email Change Logic
    const handleUpdateEmail = async () => {
        const { value: newEmail } = await Swal.fire({
            title: t('update_email'),
            input: 'email',
            inputLabel: 'Enter your new email address',
            inputPlaceholder: 'example@gmail.com',
            showCancelButton: true,
            confirmButtonColor: '#f3ba2f',
        });

        if (newEmail) {
            try {
                const userRef = ref(db, `users/${currentUser.username}`);
                await update(userRef, { email: newEmail });
                
                const updatedLocal = { ...currentUser, email: newEmail };
                localStorage.setItem('user', JSON.stringify(updatedLocal));
                setCurrentUser(updatedLocal);

                Swal.fire('Success', 'Email updated successfully!', 'success');
            } catch (e) {
                Swal.fire('Error', 'Failed to update email', 'error');
            }
        }
    };

    // 3. Custom Language Selector Logic (Fixed instance manipulation)
    const handleLanguageChange = () => {
        const langItemsHtml = languages.map(lang => `
            <div class="lang-modal-item ${lang.code === currentLang.code ? 'active-lang-item' : ''}" data-code="${lang.code}">
                <span class="lang-flag">${lang.flag}</span>
                <span class="lang-name">${lang.name}</span>
            </div>
        `).join('');

        Swal.fire({
            title: t('select_language'),
            html: `<div class="lang-modal-grid">${langItemsHtml}</div>`,
            showConfirmButton: false,
            showCloseButton: true,
            background: '#1a1a1a',
            color: '#fff',
            customClass: {
                popup: 'custom-lang-popup',
                closeButton: 'custom-close-btn'
            },
            didOpen: () => {
                const content = Swal.getHtmlContainer();
                const items = content.querySelectorAll('.lang-modal-item');
                items.forEach(item => {
                    item.addEventListener('click', () => {
                        const selectedCode = item.getAttribute('data-code');
                        const newLang = languages.find(l => l.code === selectedCode);
                        if (newLang) {
                            // Direct structural call on verified core instance
                            i18n.changeLanguage(selectedCode).then(() => {
                                setCurrentLang(newLang);
                                Swal.close();
                            });
                        }
                    });
                });
            }
        });
    };

    return (
        <div className="settings-page-wrapper">
            <div className="settings-nav">
                <MdArrowBack onClick={() => navigate(-1)} className="nav-icon" />
                <span>{t('account_settings')}</span>
                <div style={{ width: '24px' }}></div>
            </div>

            <div className="settings-content">
                <div className="settings-group-label">{t('security_privacy')}</div>
                
                {/* Change Password */}
                <div className="settings-item" onClick={handleChangePassword}>
                    <div className="settings-item-left">
                        <div className="settings-icon-bg"><MdLockOutline /></div>
                        <div className="settings-text">
                            <strong>{t('change_password')}</strong>
                            <p>{t('update_login_security')}</p>
                        </div>
                    </div>
                    <MdChevronRight className="arrow-right" />
                </div>

                {/* Update Email */}
                <div className="settings-item" onClick={handleUpdateEmail}>
                    <div className="settings-item-left">
                        <div className="settings-icon-bg"><MdEmail /></div>
                        <div className="settings-text">
                            <strong>{t('update_email')}</strong>
                            <p>{currentUser?.email || 'Change your registered email'}</p>
                        </div>
                    </div>
                    <MdChevronRight className="arrow-right" />
                </div>

                {/* Language Selection Item */}
                <div className="settings-item" onClick={handleLanguageChange}>
                    <div className="settings-item-left">
                        <div className="settings-icon-bg"><MdTranslate /></div>
                        <div className="settings-text">
                            <strong>{t('language')}</strong>
                            <p>{currentLang.flag} {currentLang.name}</p>
                        </div>
                    </div>
                    <MdChevronRight className="arrow-right" />
                </div>

                <div className="settings-info-box">
                    <p>{t('app_version')}: 2.0.4 (Stable)</p>
                    <p>{t('last_login')}: 4/23/2026</p>
                </div>
            </div>
        </div>
    );
};

export default Settings;